// src/pages/VerifyEmail.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import { CheckCircle, XCircle, RefreshCw } from "lucide-react";
import NavBar from "../components/layout/NavBar";
import Meta from "../components/Meta";
import OuterContainer from "../components/layout/OuterContainer";
import Button from "../components/common/Button";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";
import { signin, signout } from "../store/actions";
import colors from "../utils/constants/colors";

/* =============================== Styling only =============================== */

const AuthBg = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f9fc 0%, #f1f5fb 60%, #eef3fb 100%);
  padding-top: 64px; /* space for NavBar */
`;

const VerifyContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e9eef6;
  border-radius: 16px;
  margin: 48px auto;
  width: 460px;
  max-width: 95%;
  padding: 36px 32px;
  box-shadow: 0 12px 28px rgba(2, 8, 23, 0.06), 0 2px 8px rgba(2, 8, 23, 0.04);
  text-align: center;
`;

const Header = styled.h1`
  margin: 0 0 6px 0;
  text-align: center;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.2px;
  color: #0f172a;
`;

const SubHeader = styled.p`
  margin: 0 0 24px 0;
  text-align: center;
  font-size: 14px;
  color: #64748b;
`;

const StatusMessage = styled.div`
  margin: 20px 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
`;

const StatusIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 18px;
  
  &.verifying {
    background: #f0f9ff;
    border: 2px solid #e0f2fe;
    color: ${colors.primary};
    
    svg {
      animation: spin 1s linear infinite;
    }
  }
  
  &.success {
    background: #f0fdf4;
    border: 2px solid #dcfce7;
    color: #16a34a;
  }
  
  &.error {
    background: #fef2f2;
    border: 2px solid #fecaca;
    color: #dc2626;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  margin: 12px 0 20px;
  font-size: 13px;
  color: #b91c1c;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  padding: 8px 12px;
  text-align: left;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
  }
`;

/* =============== COMPONENT =============== */
const VerifyEmail = (props) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verificationState, setVerificationState] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const token = searchParams.get("token");
    
    if (!token) {
      setVerificationState("error");
      setErrorMessage("No verification token provided");
      return;
    }

    // Perform verification
    verifyEmailToken(token);
  }, [searchParams]);

  const verifyEmailToken = async (token) => {
    try {
      const endpoint = API_CONSTANT_MAP.verify_email;
      const result = await request("POST", endpoint, { token });
      
      if (result.data?.success) {
        // Verification successful - clear Redux state and redirect to sign-in
        setUserInfo(result.data.user);
        setVerificationState("success");
        
        // Clear any existing Redux state
        props.signout();
        
        // Redirect to sign-in page after 3 seconds
        setTimeout(() => {
          navigate("/signin");
        }, 3000);
      } else {
        setVerificationState("error");
        setErrorMessage(result.data?.error?.message || "Verification failed");
      }
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationState("error");
      setErrorMessage(error.error?.message || "Something went wrong during verification");
    }
  };

  const handleGoToSignIn = () => {
    navigate("/signin");
  };

  const handleGoToSignUp = () => {
    navigate("/signup");
  };

  const handleGoToSignInNow = () => {
    navigate("/signin");
  };

  return (
    <Meta pageMeta={{ title: "Verify Email - Doitly" }}>
      <NavBar user={props.state?.user} />
      <AuthBg>
        <OuterContainer>
          <VerifyContainer>
            {verificationState === "verifying" && (
              <>
                <StatusIcon className="verifying">
                  <RefreshCw size={20} />
                </StatusIcon>
                <Header>Verifying Email</Header>
                <StatusMessage>
                  Please wait while we verify your email address and create your account.
                </StatusMessage>
              </>
            )}

            {verificationState === "success" && (
              <>
                <StatusIcon className="success">
                  <CheckCircle size={20} />
                </StatusIcon>
                <Header>Email Verified</Header>
                <SubHeader>
                  Welcome to Doitly, {userInfo?.first_name}! Your account is ready.
                </SubHeader>
                <StatusMessage>
                  You can now sign in with your email and password.
                </StatusMessage>
                <ButtonGroup>
                  <Button 
                    onClick={handleGoToSignInNow}
                    style={{
                      background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                      borderRadius: "10px",
                      fontWeight: 800,
                    }}
                  >
                    Sign In Now
                  </Button>
                </ButtonGroup>
              </>
            )}

            {verificationState === "error" && (
              <>
                <StatusIcon className="error">
                  <XCircle size={20} />
                </StatusIcon>
                <Header>Verification Failed</Header>
                <SubHeader>
                  The verification link may be invalid, expired, or already used.
                </SubHeader>
                {errorMessage && (
                  <ErrorMessage>
                    {errorMessage}
                  </ErrorMessage>
                )}
                <ButtonGroup>
                  <Button 
                    onClick={handleGoToSignUp}
                    style={{
                      background: "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)",
                      borderRadius: "10px",
                      fontWeight: 800,
                    }}
                  >
                    Sign Up Again
                  </Button>
                  <Button 
                    onClick={handleGoToSignIn}
                    style={{
                      background: "transparent",
                      border: "2px solid #e9eef6",
                      color: "#64748b",
                      borderRadius: "10px",
                      fontWeight: 600,
                    }}
                  >
                    Back to Sign In
                  </Button>
                </ButtonGroup>
              </>
            )}
          </VerifyContainer>
        </OuterContainer>
      </AuthBg>
    </Meta>
  );
};

const mapStateToProps = (state) => ({ state });

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ signin, signout }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(VerifyEmail);